import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import AccountSet, { AccountSetRecommendation } from '@/types/comparison-account-set';
import { AccountSetRowKind } from '@/enums/AccountSets';

export type AccountSetMembership = {
  accountSet: AccountSet;
  kind: AccountSetRowKind;
  recommendation?: AccountSetRecommendation;
};

/**
 * Updates account set indices after an item is removed from the comparison.
 * When an item is removed, all subsequent items shift down by one index, so we need to:
 * 1. Remove references to the removed item (set to -1)
 * 2. Decrement indices of items that come after the removed item
 *
 * @param accountSets - The account sets to update
 * @param removedItemIndex - The index of the item that was removed
 * @returns Updated account sets with adjusted indices
 */
export const updateAccountSetsAfterItemRemoval = (accountSets: AccountSet[] | undefined, removedItemIndex: number): AccountSet[] | undefined => {
  if (!accountSets || accountSets.length === 0) {
    return accountSets;
  }

  return accountSets.map(set => {
    const updatedSet = { ...set };

    // Update current index
    if (updatedSet.current === removedItemIndex) {
      // Item was removed, reset to unselected
      updatedSet.current = -1;
    } else if (updatedSet.current > removedItemIndex) {
      // Item comes after removed item, shift index down
      updatedSet.current -= 1;
    }

    // Update recommendation indices
    updatedSet.recommendations = updatedSet.recommendations.map(rec => {
      if (rec.itemId === removedItemIndex) {
        // Item was removed, reset to unselected
        return { ...rec, itemId: -1 };
      }
      if (rec.itemId > removedItemIndex) {
        // Item comes after removed item, shift index down
        return { ...rec, itemId: rec.itemId - 1 };
      }
      return rec;
    });

    return updatedSet;
  });
};

export const getAccountSetMembershipForComparisonItem = (comparison: Comparison, item: ComparisonItem): AccountSetMembership | undefined => {
  const { accountSets, items } = comparison;

  if (!accountSets || accountSets.length === 0) {
    return undefined;
  }

  // eslint-disable-next-line no-underscore-dangle
  const itemIndex = items.findIndex(i => i._id === item._id);
  if (itemIndex < 0) {
    return undefined;
  }

  const currentAccountSet = accountSets.find(accountSet => accountSet.current === itemIndex);
  if (currentAccountSet) {
    return { accountSet: currentAccountSet, kind: AccountSetRowKind.CURRENT };
  }

  const recommendedAccountSet = accountSets.find(accountSet => accountSet.recommendations.some(rec => rec.itemId === itemIndex));
  if (!recommendedAccountSet) {
    return undefined;
  }

  const matchingRecommendation = recommendedAccountSet.recommendations.find(rec => rec.itemId === itemIndex);
  return { accountSet: recommendedAccountSet, kind: AccountSetRowKind.RECOMMENDED, recommendation: matchingRecommendation };
};

export const getAccountSetForComparisonItem = (comparison: Comparison, item: ComparisonItem): AccountSet | undefined =>
  getAccountSetMembershipForComparisonItem(comparison, item)?.accountSet;

export const getAccountSetNameForComparisonItem = (comparison: Comparison, item: ComparisonItem): string | undefined =>
  getAccountSetForComparisonItem(comparison, item)?.name;

/**
 * Sanitizes account sets for backend submission by removing unselected item indices (-1).
 * The frontend uses -1 to indicate "no selection" in dropdowns, but the backend expects
 * either valid indices or the field to be omitted/null.
 *
 * @param accountSets - The account sets to sanitize
 * @returns Sanitized account sets suitable for backend submission
 */
export const sanitizeAccountSetsForSave = (accountSets: AccountSet[]): AccountSet[] =>
  accountSets.map(set => {
    const sanitized: AccountSet = {
      ...set,
      // Set current to null if it's -1, otherwise keep the value
      current: set.current === -1 ? (null as any) : set.current,
      // Filter out recommendations with itemId === -1
      recommendations: set.recommendations.filter(rec => rec.itemId !== -1).map(rec => ({ ...rec })),
    };

    return sanitized;
  });
